import Menu from 'a11y/Menu';
import Popup from 'a11y/Popup';
import keyCodes from 'a11y/lib/keyCodes';
import Throttle from 'utils/Throttle';
import breakpoints from 'utils/breakpoints';
import GroupWidget from './GroupWidget';
import './topics-megamenu.scss';

/**
 * Component for the Topics Megamenu menu.
 */
export default class TopicsMegaMenu {
  /**
   * Handle arrow keys from the parent topic's link to the article list.
   *
   * @param {Object}      event       The event object.
   * @param {HTMLElement} articleList The article list element.
   */
  static handleTopicLeadKeydown(event, articleList) {
    const { UP, DOWN } = keyCodes;
    const { keyCode } = event;

    if ([UP, DOWN].includes(keyCode)) {
      event.preventDefault();
      articleList.focus();
    }
  }

  /**
   * Start the component
   */
  constructor({ element, children, options }) {
    this.element = element;
    this.children = children;
    this.options = options;

    /**
     * An array of GroupWidget items.
     * @type {Array}
     */
    this.groups = [];

    /**
     * Track article list keyboard support setup.
     * @type {Boolean}
     */
    this.articleListSupport = false;

    // Bind class methods
    this.setupArticleListSupport = this.setupArticleListSupport.bind(this);
    this.setDropdownPositioning = this.setDropdownPositioning.bind(this);
    this.init = this.init.bind(this);

    this.init();

    // Run init on breakpoint changes.
    breakpoints.lgMin.addListener(this.init);
  }

  /**
   * Set up support for using arrow keys to navigate to the Topic Menu's article lists.
   */
  setupArticleListSupport() {
    this.singlePopups.forEach((popup) => {
      const { selectors: { articleList } } = this.options;

      const articles = (
        popup.target.querySelector(articleList)
      );

      if (null !== articles) {
        // Instantiate a new GroupWidget.
        const group = new GroupWidget(articles);
        this.groups.push(group);
      }

      // Patch in arrow key navigation to article list.
      popup.firstItem.addEventListener('keydown', (event) => {
        const { UP, DOWN } = keyCodes;
        const { keyCode } = event;

        if ([UP, DOWN].includes(keyCode)) {
          event.preventDefault();
          articles.focus();
        }
      });
    });

    this.articleListSupport = true;
  }

  /**
   * Set the menubar dropdown position.
   */
  setDropdownPositioning() {
    this.singlePopups.forEach((popup) => {
      // Clear inline styles.
      popup.target.removeAttribute('style');

      if (undefined !== popup.observer) {
        // Stop observing.
        popup.observer.disconnect();
      }

      // Close the popup
      popup.setExpandedState(false);

      const controllerLeft = popup.controller.offsetLeft;
      const controllerCenter = (popup.controller.offsetWidth / 2);
      const dropdownRight = (
        window.innerWidth - (controllerLeft + controllerCenter)
      );

      // Set the dropdown's default `right` value.
      Object.assign(
        popup.target.style,
        { right: `${dropdownRight}px` },
      );

      // Create an observer instance to track `aria-hidden` attribute changes.
      popup.observer = new MutationObserver((mutationsList) => { // eslint-disable-line no-param-reassign
        mutationsList.forEach((mutation) => {
          if (
            'attributes' === mutation.type
            && 'aria-hidden' === mutation.attributeName
            && 'false' === popup.target.getAttribute('aria-hidden')
          ) {
            if ((popup.target.clientWidth / 2) < dropdownRight) {
              // Pull the dropdown to the right to center it with its controller.
              Object.assign(
                popup.target.style,
                { transform: 'translateX(50%)' },
              );
            } else {
              // The dropdown is too far right; fall back to a 30px right offset.
              Object.assign(
                popup.target.style,
                { right: '30px' },
              );
            }
          }
        });
      });

      // Start observing the target for `aria-hidden` attribute changes.
      popup.observer.observe(popup.target, { attributes: true });
    });
  }

  /**
   * Start the component.
   */
  init() {
    // The Topics Megamenu is desktop-only.
    if (breakpoints.lgMin.matches) {
      if (! (this.topicsMenu instanceof Menu)) {
        // Initialize the Menu class.
        this.topicsMenu = new Menu(this.children.list);
      }

      if (undefined === this.singlePopups) {
        const { singleDropdownLists } = this.children;

        if (null !== singleDropdownLists) {
          /**
           * Collected single-topic popups.
           * @type {Array}
           */
          this.singlePopups = Array.prototype.reduce.call(
            singleDropdownLists,
            (acc, dropdown) => {
              if (dropdown.popup instanceof Popup) {
                acc.push(dropdown.popup);
              }

              return acc;
            }, [],
          );
        }
      }

      if (! this.articleListSupport) {
        this.setupArticleListSupport();
      }

      this.setDropdownPositioning();
      this.resize = new Throttle(window, 'resize', this.setDropdownPositioning);
    } else if (this.resize instanceof Throttle) {
      this.resize.destroy();
    }
  }
}
